import { FocusTrap } from '../utils/FocusTrap';
import { getKey } from '../utils/getKey';

/**
 * This class handles the functionality of the main navigation, including
 * opening/closing sub-menus, managing accessibility features like keyboard
 * navigation and ARIA attributes, and handling mouse events on desktop.
 */
export class MainNav {
  /** @type {HTMLElement} */
  mainNavEl;

  /** @type {Array<HTMLLIElement>} */
  navListItems = [];

  /** @type {Array<HTMLLIElement | null>} */
  activeSubNavs = [];

  /** @type {HTMLElement} */
  burgerEl;

  /** @type {FocusTrap} */
  focusTrap;

  /** @type {string} */
  burgerSelector = '.burger';

  /** @type {string} */
  mainNavListSelector = '.wrap--navigation .navigation--main';

  /** @type {string} */
  listItemSelector = '.navigation-item';

  /** @type {string} */
  hasSubNavAttribute = 'data-has-subnav';

  /** @type {string} */
  isOpenAttribute = 'data-is-open';

  /** @type {string} */
  listItemLevelAttribute = 'data-nav-item-level';

  /** @type {string} */
  listItemChevronSelector = '.navigation-item__toggle-state';

  /** @type {string} */
  listSelector = '.navigation';

  /**
   * Constructs a new MainNav instance.
   * @param {HTMLElement} mainNavEl The main navigation wrapper element.
   */
  constructor(mainNavEl) {
    this.mainNavEl = mainNavEl;
    this._getNavListItems();
    this.burgerEl = document.querySelector(this.burgerSelector);

    // Initialize focus trap with CSS custom property control
    this.focusTrap = new FocusTrap(this.mainNavEl, {
      selector: `.topbar-contact button, .language-picker select, ${this.mainNavListSelector} :is(a, button), ${this.burgerSelector}`,
      customPropertyName: '--nav-focus-trap-enabled',
    });

    this.mainNavEl.addEventListener('keyup', this._handleKeyUp);
  }

  /**
   * Retrieves all navigation list items and initializes their properties and event listeners.
   * @private
   * @returns {void}
   */
  _getNavListItems = () => {
    this.navListItems = [...this.mainNavEl.querySelectorAll(this.listItemSelector)];

    if (!this.navListItems) {
      // eslint-disable-next-line no-console
      console.warn('TOUJOU: Main navigation has no list items!');
      return;
    }

    this.navListItems.forEach((listItem) => {
      // eslint-disable-next-line no-param-reassign
      listItem.hasSubNav = listItem.hasAttribute(this.hasSubNavAttribute);
      if (!listItem.hasSubNav) return;

      // eslint-disable-next-line no-param-reassign
      listItem.isOpen = false;
      // eslint-disable-next-line no-param-reassign
      listItem.level = listItem.getAttribute(this.listItemLevelAttribute);
      // eslint-disable-next-line no-param-reassign
      listItem.toggleEl = listItem.querySelector(this.listItemChevronSelector);
      listItem.toggleEl.addEventListener('click', () => {
        this._toggleListItemState(listItem);
      });
      listItem.addEventListener('keyup', this._handleKeyUp);
    });
  };

  /**
   * Handles keyboard events (keyup) for closing menus with the Escape key.
   * @private
   * @param {KeyboardEvent} event The keyboard event object.
   * @returns {void}
   */
  _handleKeyUp = (event) => {
    if (getKey(event).isEscape) {
      const listItem = this.activeSubNavs.pop();

      event.stopPropagation();

      if (!listItem) {
        if (this.burgerEl.getAttribute('aria-pressed') !== 'true') return;

        this.burgerEl.click();
      } else {
        this._toggleListItemState(listItem);

        listItem.parentElement?.querySelector(
          `${this.listItemChevronSelector}[aria-controls="${listItem.querySelector(this.listSelector)?.id}"]`,
        )?.focus({ focusVisible: true });
      }
    }
  };

  /**
   * Toggles the open/closed state of a navigation list item.
   * @private
   * @param {HTMLLIElement} listItem The list item to toggle.
   * @returns {void}
   */
  _toggleListItemState = (listItem) => {
    const listItemChevron = listItem.querySelector(this.listItemChevronSelector);
    const listItemLevel = parseInt(listItem.getAttribute('data-nav-item-level'), 10);

    // eslint-disable-next-line no-param-reassign
    listItem.isOpen = !listItem.isOpen;

    listItemChevron?.setAttribute('aria-expanded', `${listItem.isOpen}`);
    listItemChevron?.setAttribute('aria-pressed', `${listItem.isOpen}`);

    if (listItem.isOpen) {
      listItem.setAttribute(this.isOpenAttribute, '');

      this._closeOtherOpenListItems(listItem);
      this.activeSubNavs[listItemLevel - 1] = listItem;
      window.addEventListener('click', this._onWindowClick);
    } else {
      listItem.removeAttribute(this.isOpenAttribute);
      this.activeSubNavs[listItemLevel - 1] = null;
      if (this.activeSubNavs.length === listItemLevel) {
        this.activeSubNavs.pop();
      }
    }
  };

  /**
   * Closes other open sub-navigation items at the same level as the provided list item.
   * @private
   * @param {HTMLLIElement} listItem The list item to check against its siblings.
   * @returns {void}
   */
  _closeOtherOpenListItems = (listItem) => {
    const openSiblings = listItem.parentNode?.querySelectorAll(`${this.listItemSelector}[${this.isOpenAttribute}]`);

    if (!openSiblings) return;

    openSiblings.forEach((sibling) => {
      if (sibling.isOpen && sibling !== listItem) this._toggleListItemState(sibling);
    });
  };

  /**
   * Closes all open navigation list items.
   * @private
   * @returns {void}
   */
  _closeAllNavListItems = () => {
    this.navListItems.forEach((listItem) => {
      if (listItem.isOpen) {
        this._toggleListItemState(listItem);
        window.removeEventListener('click', this._onWindowClick);
      }
    });
  };

  /**
   * Handles a click event on the window to close the menu if the click is outside the navigation.
   * @private
   * @param {MouseEvent} event The click event object.
   * @returns {void}
   */
  _onWindowClick = (event) => {
    if (!event.target.closest(this.mainNavListSelector)) {
      this._closeAllNavListItems();
    }
  };
}

/**
 * Initializes the main navigation by creating a new MainNav instance.
 * @returns {void}
 */
export function initMainNav() {
  const mainNavEl = document.querySelector('.wrap--navigation');

  if (!mainNavEl || mainNavEl.mainNav) return;

  mainNavEl.mainNav = new MainNav(mainNavEl);
}
